import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir163jTaggingService } from './fir-163j-tagging.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService as GtwGlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import {
  GeneralConfig,
  GlobalService,
  FilterParams,
  LoadProjectsParams,
  Project,
  Entity,
  RowDataItem,
  GridDataItem,
  ProjectTagPayload,
  SystemProjectTagPayload,
  ModalMode
} from './fir-163j-tagging.types';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'gtw-fir-163j-tagging',
    templateUrl: './fir-163j-tagging.component.html',
    styleUrls: ['./fir-163j-tagging.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Fir163jTaggingComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private subscriptions = new Subscription();

  enableButton = true;
  crudLoading = false;
  minimise = false;
  title = "163j Project Tag";

  entities: Entity[] = [];
  projectList: Project[] = [];
  project: Project | null = null;
  projectLoading = true;
  sysProjectLoading = true;
  tagProjectLoading = false;
  modalName: ModalMode = '';

  GENERAL_CONFIG: GeneralConfig = {} as GeneralConfig;
  logged_in_user = '';
  globalService: GlobalService = {};
  filterParams: FilterParams = {};
  gridData: GridDataItem | GridDataItem[] = {};

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  /** Optional bridge when `modalScreen.modal_name` is not on hydrated workspace params. */
  @Input('modal-name')
  set _modal_name(modalName: string | ModalMode) {
    try {
      if (typeof modalName === 'string' && modalName) {
        this.modalName = modalName as ModalMode;
        this.setModalMode();
      }
    } catch (e) {
      console.error('ERROR parsing modal-name:', e);
    }
  }

  constructor(
    private fir163jTaggingService: Fir163jTaggingService,
    private gtwSnackbarService: GtwSnackbarService,
    private snacks: MatSnackBar,
    private gtwGlobal: GtwGlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit() {
    this.applyWorkspaceContext();
    this.processGridData();
    this.init();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.processGridData();
        this.init();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.processGridData();
        this.init();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal) as GeneralConfig;
    this.GENERAL_CONFIG = {
      ...gc,
      api: gc.api || gc.base_url || ''
    };
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    const baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    const sso = this.gtwApp?.USER_SETTINGS?.user?.sso_id;
    this.logged_in_user = sso != null && sso !== '' ? String(sso) : this.logged_in_user;
    this.globalService = {
      sso_id: this.logged_in_user,
      globalParams: baseGlobalParams,
      filterParams: this.filterParams
    };

    const modalFromWs = baseGlobalParams?.modalScreen?.modal_name as ModalMode | undefined;
    this.modalName = (modalFromWs || this.modalName) as ModalMode;
    this.setModalMode();

    const sel = activeScreen?.data?.selected;
    const g = gridPayloadFromActiveScreen(activeScreen);
    const rp = rowPayloadFromActiveScreen(activeScreen);

    let nextGrid: GridDataItem | GridDataItem[] | null = sel != null ? sel : g;
    if (
      nextGrid == null ||
      (typeof nextGrid === 'object' &&
        !Array.isArray(nextGrid) &&
        Object.keys(nextGrid as object).length === 0)
    ) {
      if (Array.isArray(rp) && rp.length > 0) {
        nextGrid = rp as GridDataItem[];
      } else if (rp && typeof rp === 'object' && !Array.isArray(rp) && Object.keys(rp).length > 0) {
        const raw = rp as RowDataItem;
        nextGrid = (raw as any).data != null ? (raw as any) : { rowData: raw };
      }
    }
    if (nextGrid != null) {
      this.gridData = nextGrid;
    }
  }

  init() {
    const apiRoot = this.GENERAL_CONFIG?.api || this.GENERAL_CONFIG?.base_url || '';
    if (apiRoot && this.globalService?.globalParams) {
      this.loadProjects();
    } else {
      this.projectLoading = false;
    }
  }

  setModalMode() {
    if (this.modalName === 'taggingsys163jModal') {
      this.sysProjectLoading = false;
      this.tagProjectLoading = true;
    }
  }

  processGridData(): void {
    if (!this.gridData) return;

    let processedGridData: Entity[];

    if (!_.isArray(this.gridData)) {
      const gridItem = this.gridData as GridDataItem;
      const obj: Entity = {
        row_id: gridItem.rowData?.object_id,
        data: gridItem.rowData as any
      };
      processedGridData = [obj];
    } else {
      processedGridData = this.gridData as Entity[];
    }

    this.entities = _.clone(processedGridData);
  }

  /** Build payload for loadProjects (action_code r5ixq2). */
  private buildLoadProjectsParams(): LoadProjectsParams {
    return {
      action_code: 'r5ixq2',
      activity_key: this.filterParams.activity_key ?? this.globalService.globalParams?.activity_key ?? '',
      filing_key: this.filterParams.filing_key ?? this.globalService.globalParams?.filing_key ?? '1',
      filter_by: this.filterParams.filter_by ?? this.globalService.globalParams?.filter_by ?? '9',
      is_issue_key_enabled: this.filterParams.is_issue_key_enabled ?? this.globalService.globalParams?.is_issue_key_enabled ?? 'N',
      is_locked: this.filterParams.is_locked ?? this.globalService.globalParams?.is_locked ?? 'N',
      issue_id: this.filterParams.issue_id ?? this.globalService.globalParams?.issue_id ?? '',
      issue_short_desc: this.filterParams.issue_short_desc ?? this.globalService.globalParams?.issue_short_desc ?? '',
      jcd_key: this.filterParams.jcd_key ?? this.globalService.globalParams?.jcd_key ?? '250',
      jcd_ta_key: this.filterParams.jcd_ta_key ?? this.globalService.globalParams?.jcd_ta_key ?? '',
      parent_me_string: this.filterParams.parent_me_string ?? this.globalService.globalParams?.parent_me_string ?? '',
      scenario: this.filterParams.scenario ?? this.globalService.globalParams?.scenario ?? '',
      scenarioCode: this.filterParams.scenarioCode ?? this.globalService.globalParams?.scenarioCode ?? 'RAF',
      screen_key: this.filterParams.screen_key ?? this.globalService.globalParams?.screen_key ?? '',
      screen_type: this.filterParams.screen_type ?? this.globalService.globalParams?.screen_type ?? 'grid',
      system: this.filterParams.system ?? this.globalService.globalParams?.system ?? 'A',
      tax_year: this.filterParams.tax_year ?? this.globalService.globalParams?.tax_year ?? ''
    };
  }

  loadProjects() {
    this.projectLoading = true;
    const params = this.buildLoadProjectsParams();
    const apiRoot = this.GENERAL_CONFIG.api || this.GENERAL_CONFIG.base_url || '';

    this.subscriptions.add(
      this.fir163jTaggingService.loadProjects(apiRoot, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (data: any) => {
            if (data.callSuccess !== "1") {
              this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error loading projects', 'gtw-snackbar--red');
              this.projectLoading = false;
            } else {
              this.projectList = data.jsonObject || [];
              this.projectLoading = false;

              const untag = { ACCT_KEY: null, TAG_163J_DESC: "Remove 163j Project Tag" };
              this.projectList.unshift(untag);

              this.setProject();
            }
          },
          (error) => {
            this.gtwSnackbarService.openSnackBar('Error loading projects', 'gtw-snackbar--red');
            this.projectLoading = false;
          }
        )
    );
  }

  setProject() {
    try {
      const projectArray: any[] = [];
      _.forEach(this.entities, (value) => {
        const acct_key = value.data?.ACCT_KEY;
        if (!_.includes(projectArray, acct_key)) {
          projectArray.push(acct_key);
        }
      });

      if (projectArray.length === 1) {
        const project = projectArray[0];
        this.project = _.find(this.projectList, { 'ACCT_KEY': project }) || null;
      }
    } catch (e) {
      console.info(e);
    }
  }

  removeEntity(index: number) {
    this.entities.splice(index, 1);
    if (this.entities.length === 0) {
      this.cancel();
    }
  }

  save() {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;

    if (this.modalName === 'taggingsys163jModal') {
      this.saveSystemProjectTag();
    } else {
      this.saveProjectTag();
    }
  }

  saveProjectTag() {
    const sendobj = this.buildProjectTagJson();
    const apiRoot = this.GENERAL_CONFIG.api || this.GENERAL_CONFIG.base_url || '';

    this.subscriptions.add(
      this.fir163jTaggingService.saveProjectTag(
        apiRoot,
        sendobj,
        this.globalService?.globalParams || {},
        this.filterParams
      ).pipe(takeUntil(this.destroy$))
        .subscribe(
          (result: any) => {
            this.crudLoading = false;

            if (result.callSuccess !== "1") {
              this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error saving project tag', 'gtw-snackbar--red');
            } else {
              this.gtwSnackbarService.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
              this.requestGridUpdate.emit('refresh');
            }
          },
          (error) => {
            this.crudLoading = false;
            this.gtwSnackbarService.openSnackBar('Error saving project tag', 'gtw-snackbar--red');
          }
        )
    );
  }

  saveSystemProjectTag() {
    const sendobj = this.buildSystemProjectTagJson();
    const apiRoot = this.GENERAL_CONFIG.api || this.GENERAL_CONFIG.base_url || '';

    this.subscriptions.add(
      this.fir163jTaggingService.saveSystemProjectTag(
        apiRoot,
        sendobj,
        this.globalService?.globalParams || {},
        this.filterParams
      ).pipe(takeUntil(this.destroy$))
        .subscribe(
          (result: any) => {
            this.crudLoading = false;

            if (result.callSuccess !== "1") {
              this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error saving system project tag', 'gtw-snackbar--red');
            } else {
              this.gtwSnackbarService.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
              this.requestGridUpdate.emit('refresh');
            }
          },
          (error) => {
            this.crudLoading = false;
            this.gtwSnackbarService.openSnackBar('Error saving system project tag', 'gtw-snackbar--red');
          }
        )
    );
  }

  buildProjectTagJson(): ProjectTagPayload[] {
    const sendobj: ProjectTagPayload[] = [];

    if (this.entities.length > 0) {
      this.entities.forEach((row) => {
        const projectTagjson: ProjectTagPayload = {} as ProjectTagPayload;
        const dataObj = row.data;

        projectTagjson['COMBINATION_KEY'] = dataObj.COMBINATION_KEY;
        projectTagjson['TAX_YEAR'] = dataObj.TAX_YEAR;
        projectTagjson['SCENARIO'] = this.globalService?.globalParams?.scenario;
        projectTagjson['JCD_KEY'] = this.globalService?.globalParams?.jcd_key;

        if (this.project != null) {
          projectTagjson['NEW_163J_TAG'] = this.project.TAG_163J;
          projectTagjson['NEW_163J_ACCT_KEY'] = this.project.ACCT_KEY;
        }

        projectTagjson['TRANSHEADER_KEY'] = dataObj.TRANS_HEADER_KEY;
        projectTagjson['TRANSDETAILS_KEY'] = dataObj.TRANS_DETAILS_KEY;
        projectTagjson['LOCAL_ACCT_KEY'] = dataObj.LOCAL_ACCT_KEY;

        sendobj.push(projectTagjson);
      });
    }

    return sendobj;
  }

  buildSystemProjectTagJson(): SystemProjectTagPayload[] {
    const sendobj: SystemProjectTagPayload[] = [];

    if (this.entities.length > 0) {
      this.entities.forEach((row) => {
        const projectTagSysjson: SystemProjectTagPayload = {} as SystemProjectTagPayload;
        const dataObj = row.data;

        projectTagSysjson['TAX_YEAR'] = dataObj.TAX_YEAR;
        projectTagSysjson['SCENARIO'] = this.globalService?.globalParams?.scenario;
        projectTagSysjson['JCD_KEY'] = this.globalService?.globalParams?.jcd_key;

        if (this.project != null) {
          projectTagSysjson['NEW_163J_TAG'] = this.project.TAG_163J;
          projectTagSysjson['NEW_163J_ACCT_KEY'] = this.project.ACCT_KEY;
        }

        projectTagSysjson['SYSTEM_ACCT_KEY'] = dataObj.SYS_ACCT_KEY;
        projectTagSysjson['SYS_CHART_CODE'] = dataObj.SYS_CHART_CODE;
        projectTagSysjson['SYS_CHART_KEY'] = dataObj.SYS_CHART_KEY;

        sendobj.push(projectTagSysjson);
      });
    }

    return sendobj;
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  modalClose() {
    this.requestGridUpdate.emit('cancel');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
